import {
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';

import {
  BitbucketProjectsApi,
  BitbucketProjectsResponse,
} from './BitbucketProjectsApi';

/**
 * Client implementation for Bitbucket Projects API
 */
export class BitbucketProjectsClient implements BitbucketProjectsApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: {
    discoveryApi: DiscoveryApi;
    fetchApi: FetchApi;
  }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  async getProjects(workspace: string, refresh: boolean = false): Promise<BitbucketProjectsResponse> {
    try {
      // Use discoveryApi to get the base URL dynamically
      const baseUrl = await this.discoveryApi.getBaseUrl('bitbucket-projects');
      // Add refresh query parameter to bypass cache
      const url = `${baseUrl}/projects/${workspace}${refresh ? '?refresh=true' : ''}`;

      const response = await this.fetchApi.fetch(url);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(
          errorData.message ||
            `Failed to fetch projects: ${response.statusText}`,
        ) as any;
        // DO-4126: Preserve status code from response or error data for error type detection
        // Check both response.status and errorData.statusCode (backend returns statusCode in JSON)
        error.status = errorData.statusCode || response.status;
        error.statusCode = errorData.statusCode || response.status;
        error.response = response;
        throw error;
      }

      const data: BitbucketProjectsResponse = await response.json();
      return data;
    } catch (error: any) {
      // DO-4126: Preserve status code and response for error type detection
      const newError = new Error(
        `Failed to fetch Bitbucket projects: ${error.message}`,
      ) as any;
      // Preserve original error properties (status, response) for error type detection
      if (error.status) newError.status = error.status;
      if (error.response) newError.response = error.response;
      throw newError;
    }
  }
}
